import type { Effect, GameState, Modifiers, ResourceId } from './types';
import {
  ANCESTRAL_BY_ID,
  BALANCE,
  BUILDINGS_BY_ID,
  RESOURCE_IDS,
  TECHS_BY_ID,
  getAge,
} from './content';

const STORAGE_WILDCARD = '*';

function emptyModifiers(): Modifiers {
  return {
    global: 0,
    perResource: {},
    perJob: {},
    flatProduction: {},
    storageFlat: {},
    storageMult: {},
    shelter: BALANCE.baseShelter,
    nutrition: 1,
    consumption: 1,
    growth: 1,
    akGain: 1,
    vigorPerStack: BALANCE.vigorBasePerStack,
    vigorMaxStacks: BALANCE.vigorBaseStacks,
    offlineHours: BALANCE.baseOfflineHours,
    taleLuck: 0,
    startPopulation: BALANCE.baseStartPopulation,
    startResources: {},
    startTechs: [],
  };
}

function add(target: Record<string, number>, key: string, amount: number): void {
  target[key] = (target[key] ?? 0) + amount;
}

/** Apply one effect `count` times into the accumulator. */
function applyEffect(accumulator: Modifiers, effect: Effect, count: number): void {
  if (count === 0) return;

  switch (effect.kind) {
    case 'globalMult':
      accumulator.global += effect.amount * count;
      break;
    case 'resourceMult':
      add(accumulator.perResource, effect.resource, effect.amount * count);
      break;
    case 'jobMult':
      add(accumulator.perJob, effect.job, effect.amount * count);
      break;
    case 'flatProduction':
      add(accumulator.flatProduction, effect.resource, effect.amount * count);
      break;
    case 'storage':
      add(accumulator.storageFlat, effect.resource, effect.amount * count);
      break;
    case 'storageMult':
      add(accumulator.storageMult, effect.resource, effect.amount * count);
      break;
    case 'shelter':
      accumulator.shelter += effect.amount * count;
      break;
    case 'nutritionMult':
      accumulator.nutrition += effect.amount * count;
      break;
    case 'consumptionMult':
      accumulator.consumption += effect.amount * count;
      break;
    case 'growthMult':
      accumulator.growth += effect.amount * count;
      break;
    case 'akGainMult':
      accumulator.akGain += effect.amount * count;
      break;
    case 'vigorMult':
      accumulator.vigorPerStack += BALANCE.vigorBasePerStack * effect.amount * count;
      break;
    case 'vigorStacks':
      accumulator.vigorMaxStacks += effect.amount * count;
      break;
    case 'offlineHours':
      accumulator.offlineHours += effect.amount * count;
      break;
    case 'taleLuck':
      accumulator.taleLuck += effect.amount * count;
      break;
    case 'startPopulation':
      accumulator.startPopulation += effect.amount * count;
      break;
    case 'startResource':
      add(accumulator.startResources as Record<string, number>, effect.resource, effect.amount * count);
      break;
    case 'startTech':
      if (!accumulator.startTechs.includes(effect.tech)) accumulator.startTechs.push(effect.tech);
      break;
  }
}

/**
 * Collect every active modifier from ancestral nodes, technologies, buildings,
 * temporary buffs and Vigor. Called once per tick and once per UI render.
 */
export function collectModifiers(state: GameState): Modifiers {
  const accumulator = emptyModifiers();

  // Ancestral Memory — permanent, scales with node level.
  for (const [nodeId, level] of Object.entries(state.ancestral)) {
    const node = ANCESTRAL_BY_ID[nodeId];
    if (!node || level <= 0) continue;
    for (const effect of node.effects) applyEffect(accumulator, effect, level);
  }

  // Technologies — known for the length of the generation.
  for (const techId of state.techs) {
    const tech = TECHS_BY_ID[techId];
    if (!tech) continue;
    for (const effect of tech.effects) applyEffect(accumulator, effect, 1);
  }

  // Buildings — scale with the number owned.
  for (const [buildingId, owned] of Object.entries(state.buildings)) {
    const building = BUILDINGS_BY_ID[buildingId];
    if (!building || owned <= 0) continue;
    for (const effect of building.effects) applyEffect(accumulator, effect, owned);
  }

  // Temporary buffs from tales.
  for (const buff of state.buffs) {
    accumulator.global += buff.amount;
  }

  // Vigor from active play.
  accumulator.global += state.vigorStacks * accumulator.vigorPerStack;

  // Guard rails so no modifier can invert a rate.
  accumulator.consumption = Math.max(0.1, accumulator.consumption);
  accumulator.nutrition = Math.max(0.1, accumulator.nutrition);
  accumulator.growth = Math.max(0, accumulator.growth);
  accumulator.shelter = Math.max(1, accumulator.shelter);

  return accumulator;
}

/** Multiplier applied to a single resource's production. */
export function resourceMultiplier(
  state: GameState,
  modifiers: Modifiers,
  resourceId: ResourceId,
): number {
  const era = getAge(state.ageIndex).eraMultiplier;
  const combined = 1 + modifiers.global + (modifiers.perResource[resourceId] ?? 0);
  return Math.max(0, combined) * era;
}

/** Multiplier applied to a single job's output. */
export function jobMultiplier(modifiers: Modifiers, jobId: string): number {
  return Math.max(0, 1 + (modifiers.perJob[jobId] ?? 0));
}

/** Effective storage cap for a resource, including the per-age scaling. */
export function storageCap(
  state: GameState,
  modifiers: Modifiers,
  resourceId: ResourceId,
  baseCap: number,
): number {
  const flat = modifiers.storageFlat[resourceId] ?? 0;
  const multiplier =
    1 +
    (modifiers.storageMult[resourceId] ?? 0) +
    (modifiers.storageMult[STORAGE_WILDCARD] ?? 0);
  const ageScale = Math.pow(BALANCE.storagePerAge, state.ageIndex);
  return (baseCap + flat) * Math.max(0.1, multiplier) * ageScale;
}

/** Every storage cap at once, keyed by resource. */
export function allStorageCaps(
  state: GameState,
  modifiers: Modifiers,
  baseCaps: Record<ResourceId, number>,
): Record<ResourceId, number> {
  const caps: Record<ResourceId, number> = {};
  for (const resourceId of RESOURCE_IDS) {
    caps[resourceId] = storageCap(state, modifiers, resourceId, baseCaps[resourceId] ?? 0);
  }
  return caps;
}
